import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedService } from '@cfp/core';
import { Button, CfpTranslateModule, InputCheckbox, SidePanel } from '@cfp/ui';
import { AlertService } from '@cfp/ui/feedback';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingTagDTO, TagsService } from 'csr-asset-app';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TagsToolbar } from '../tags-toolbar';

@Component({
  selector: 'cfp-tags-building-modal',
  templateUrl: './tags-building-modal.html',
  imports: [
    TagsToolbar,
    Button,
    SidePanel,
    TranslateModule,
    CfpTranslateModule,
    FormsModule,
    InputCheckbox,
    ReactiveFormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagsBuildingModal implements OnInit {
  public portfolioId: number;
  public buildingId: number;

  protected form: FormGroup<{
    tags: FormArray<FormControl<boolean>>;
  }>;

  protected readonly searchText = signal<string | undefined>(undefined);
  protected readonly tags = signal<BuildingTagDTO[] | undefined>(undefined);

  protected readonly tagsService = inject(TagsService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly alertService = inject(AlertService);
  private readonly sharedService = inject(SharedService);
  private readonly formBuilder = inject(FormBuilder);

  private readonly filteredTags = computed(() => {
    const tags = this.tags();
    const searchText = this.searchText()?.toLowerCase();
    if (!tags || !searchText) {
      return tags;
    }
    return tags.filter((tag) => tag.tag.toLowerCase().includes(searchText));
  });

  public ngOnInit(): void {
    this.fetchTags();
  }

  protected getTagFormControl(index: number): FormControl {
    return this.form.controls.tags.get([index]) as FormControl;
  }

  protected fetchTags(): void {
    this.tagsService.v2BuildingsBuildingIdTagsGet(this.buildingId).subscribe({
      next: (tags) => this.initTags(tags),
      error: () => {
        this.alertService.showAlert('error', 'error.general');
      }
    });
  }

  protected close(): void {
    this.bsModalRef.hide();
  }

  protected shouldShowTag(tag: BuildingTagDTO): boolean {
    if (!this.searchText()) {
      return true;
    }
    return this.filteredTags()?.some((filteredTag) => filteredTag.tagId === tag.tagId) ?? false;
  }

  protected search(searchText: any): void {
    this.searchText.set(searchText);
  }

  protected saveAndClose() {
    if (this.form.invalid) {
      return;
    }

    const updatedTags = this.getTagsFromForm();
    if (!updatedTags) {
      return;
    }
    this.tagsService.v2BuildingsBuildingIdTagsPut(updatedTags, this.buildingId).subscribe({
      next: () => {
        this.sharedService.emitReloadComponent();
        this.close();
      },
      error: () => {
        this.alertService.showAlert('error', 'error.general');
      }
    });
  }

  private getTagsFromForm(): BuildingTagDTO[] | undefined {
    const tags = this.tags();
    if (!this.form || !tags) {
      return undefined;
    }

    const formValues = this.form.value.tags;
    return [...tags].map((tag, index) => ({
      ...tag,
      enabled: formValues?.[index] || false
    }));
  }

  private initTags(tags: BuildingTagDTO[]): void {
    const existingTags = this.getTagsFromForm();

    let newTags: BuildingTagDTO[] = tags;
    if (existingTags) {
      newTags = newTags
        .sort((a, b) => a.tag.localeCompare(b.tag))
        .map((tag) => {
          tag.enabled = existingTags.some((existingTag) => existingTag.tagId === tag.tagId && existingTag.enabled);
          return tag;
        });
    }
    this.tags.set(newTags);
    this.buildForm(newTags);
  }

  private buildForm(tags: BuildingTagDTO[]): void {
    const tagFormArray: FormArray = new FormArray(
      tags.map((tag: BuildingTagDTO) => {
        return new FormControl(tag.enabled);
      })
    );
    this.form = this.formBuilder.group({
      tags: tagFormArray
    });
  }
}
